import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { JobOffer } from "@ckgo2/shared/types";
import { Plus, Pencil, Trash2, Loader2, Briefcase } from "lucide-react";

export default function JobOffersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["job-offers"],
    queryFn: () => api.get<ApiResponse<JobOffer[]>>("/api/admin/job-offers"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/job-offers/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["job-offers"] }),
  });

  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
  };

  const offers = data?.data || [];

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.jobOffers.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("content.jobOffers.list.subtitle")}
          </p>
        </div>
        <Link to="/job-offers/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("content.jobOffers.list.addOffer")}
        </Link>
      </div>

      {offers.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center dark:border-gray-700">
          <Briefcase className="mx-auto mb-3 h-8 w-8 text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400">{t("content.jobOffers.list.empty")}</p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-200 dark:divide-gray-700">
          {offers.map((offer) => (
            <div key={offer.id} className="flex items-center justify-between py-3">
              <div className="min-w-0 flex-1 pr-4">
                <p className="truncate font-medium text-gray-900 dark:text-white">{offer.title}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("content.jobOffers.list.meta", {
                    date: offer.publishedDate || "—",
                    order: offer.sortOrder,
                    status: statusLabels[offer.status] || offer.status,
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/job-offers/${offer.id}`}
                  className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                >
                  <Pencil className="h-4 w-4" />
                </Link>
                <button
                  title={t("common.actions.delete")}
                  onClick={() => {
                    if (confirm(t("content.jobOffers.list.confirmDelete")))
                      deleteMutation.mutate(offer.id);
                  }}
                  className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
